import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import { Body } from '@/components/Typography';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import routes from '@/routes/routes';

type Props = {
  payrollId: string;
  hideModal: () => void;
};

const ConfirmDeletePayrollModal = ({ payrollId, hideModal }: Props) => {
  const navigate = useNavigate();
  const [error, setError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleDeletePayroll = async () => {
    setIsLoading(true);
    setError(false);
    try {
      await axiosRubyClient.delete(`/api/payrolls/${payrollId}`);
      navigate(`/${routes.payrollsV2}`);
    } catch (err) {
      console.error('Error deleting payroll:', err);
      setError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title="Are you sure you want to delete this payroll?"
      onRequestClose={hideModal}
    >
      <Card.Section>
        {error && (
          <Alert
            description="Something went wrong, please contact support"
            icon={faExclamationTriangle}
            status="danger"
            title="An error has occurred"
          />
        )}
        <Body>
          To see this payroll again, you would have to run new payroll.
        </Body>
        <Stack css={{ marginTop: '10px' }} gap={10} justify="end">
          <Button
            a11yLabel="deny"
            appearance="outline"
            label="Cancel"
            status="danger"
            type="button"
            onClick={hideModal}
          />

          <Button
            a11yLabel="accept"
            isLoading={isLoading}
            label="Delete"
            type="submit"
            onClick={handleDeletePayroll}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default ConfirmDeletePayrollModal;
